"use client";

import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { ScrollReveal } from "./ScrollReveal";
import { LandingSectionHeader } from "./LandingSectionHeader";
import { FREE_TOOLS } from "@/components/freeTools/FreeToolsNav";

/**
 * Marketing landing's on-ramp to the free, no-account tool set at `/free`.
 * The signed-out landing otherwise has zero links there — this section (and
 * the "Free tools" header nav item) is the only path in for a server owner
 * who isn't ready to subscribe yet.
 *
 * Reuses `FREE_TOOLS` from FreeToolsNav (the same registry `/free` and every
 * `/free/*` page cross-link from) rather than re-declaring the tool list, so
 * a new free tool only needs to be added once.
 */
export function LandingFreeTools() {
  return (
    <section className="py-8 sm:py-14">
      <div className="lb-container">
        <ScrollReveal>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <LandingSectionHeader
              eyebrow="Free tools"
              title="No account. No subscription. Runs in your browser."
              copy="Server-owner utilities for the decisions you make before you buy a tool — config order, spawn codes, id collisions, download size."
            />
            <Link
              to="/free"
              className="group hidden shrink-0 items-center gap-1 pb-1 text-sm font-medium text-[hsl(var(--lb-muted))] transition hover:text-[hsl(var(--lb-ink))] sm:inline-flex"
            >
              See all free tools
              <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
            </Link>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={80}>
          <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {FREE_TOOLS.map((tool) => {
              const Icon = tool.icon;
              return (
                <Link
                  key={tool.key}
                  to={tool.href}
                  className="group flex items-start gap-3 rounded-2xl border border-[hsl(var(--lb-line))] bg-[hsl(var(--lb-panel)/0.5)] p-4 transition duration-200 hover:border-[hsl(var(--lb-line)/1.4)] hover:bg-[hsl(var(--lb-panel))]"
                >
                  <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-[hsl(var(--lb-line))] bg-[hsl(var(--lb-bg-soft))] text-[hsl(var(--lb-muted))] transition duration-200 group-hover:border-[hsl(var(--lb-ink)/0.3)] group-hover:bg-[hsl(var(--lb-ink))] group-hover:text-[hsl(var(--lb-bg))]">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5 text-base font-semibold tracking-tight text-[hsl(var(--lb-ink)/0.85)]">
                      {tool.label}
                      <ArrowUpRight className="h-3.5 w-3.5 shrink-0 -translate-x-1 text-[hsl(var(--lb-muted))] opacity-0 transition duration-300 group-hover:translate-x-0 group-hover:opacity-100" />
                    </span>
                    <span className="mt-1.5 block text-[12.5px] leading-relaxed text-[hsl(var(--lb-muted))]">
                      {tool.blurb}
                    </span>
                  </span>
                </Link>
              );
            })}
          </div>
        </ScrollReveal>

        <div className="mt-6 sm:hidden">
          <Link
            to="/free"
            className="group inline-flex items-center gap-1 text-sm font-medium text-[hsl(var(--lb-muted))] transition hover:text-[hsl(var(--lb-ink))]"
          >
            See all free tools
            <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}
